import { MoreHoriz } from '@mui/icons-material';
import {
  Box,
  Breadcrumbs,
  Card,
  CardActionArea,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { useWorkspaceId } from 'api/providers/workspaceProvider';
import { FinsightEntityFieldValue } from 'components/renderField';
import React, { Ref } from 'react';
import { FinsightEntity } from 'shared';

export type EntityCardViewProps = {
  /** Entity to display */
  entity: FinsightEntity;
  /** Search params to append to URL */
  searchParams: string;
  /** Whether to hide parent information */
  hideParent?: boolean;
  /** Whether the parent controls the card size */
  parentControlledCardSize?: boolean;
  /** Whether to disable actions on the entity */
  disableActions?: boolean;
  /** Ref for the card element */
  elemRef?: Ref<HTMLDivElement>;
  /** Handler for context menu */
  onContextMenu: (e: React.MouseEvent) => void;
  /** Handler for drag start */
  onDragStart: (e: React.DragEvent) => void;
  /** Handler for more button click */
  onMoreButtonClick: (e: React.MouseEvent) => void;
};

export function EntityCardView(props: EntityCardViewProps) {
  const {
    entity,
    searchParams,
    hideParent,
    parentControlledCardSize,
    disableActions,
    elemRef,
    onContextMenu,
    onDragStart,
    onMoreButtonClick,
  } = props;

  const workspaceId = useWorkspaceId();

  return (
    <Card
      ref={elemRef}
      variant="outlined"
      key={entity.id}
      sx={{
        textShadow: '0 0 3px rgba(0,0,0,0.7)',
        ...(!parentControlledCardSize
          ? { width: 275, height: 250, flexShrink: 0 }
          : {}),
      }}
      onContextMenu={onContextMenu}
      onDragStart={onDragStart}
    >
      <Box
        height="100%"
        display="grid"
        gridTemplateRows="1fr"
        gridTemplateColumns="1fr"
      >
        {entity.extraFieldsMeta['image'] &&
          entity.extraFieldsMeta['image'].schema.type === 'image' &&
          entity.extraFieldsMeta['image'].schema.ui?.visibility?.card && (
            <Box
              height="100%"
              minHeight={0}
              gridRow={1}
              gridColumn={1}
              sx={{
                pointerEvents: 'none',
                '& > *': {
                  height: '100%',
                },
                '& > * > *': {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                },
                '& > * > span': {
                  fontSize: '150px',
                },
              }}
            >
              <FinsightEntityFieldValue
                field={entity.extraFieldsMeta['image']}
                value={entity.extraFields['image']}
                viewType="card_bg"
              />
            </Box>
          )}
        <Stack gridRow={1} gridColumn={1}>
          <CardActionArea
            disableRipple
            disabled={disableActions}
            href={`/workspace/${workspaceId}/entity/${entity.id}?${searchParams}`}
            sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden' }}
          >
            <Stack p={2}>
              <Typography variant="h5">
                {entity.name.length > 40
                  ? `${entity.name.slice(0, 40)}...`
                  : entity.name}
              </Typography>
              {Object.values(entity.extraFieldsMeta).map((field, index) => (
                <FinsightEntityFieldValue
                  key={index}
                  field={field}
                  value={entity.extraFields[field.name]}
                  viewType="card"
                />
              ))}
            </Stack>
          </CardActionArea>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            px={2}
            py={0.5}
            flexShrink={0}
            borderTop="1px solid"
            borderColor="divider"
            sx={{
              backdropFilter: 'blur(10px) brightness(0.8) saturate(0.7)',
            }}
          >
            {!hideParent && (
              <Breadcrumbs>
                {entity.parents?.toReversed().map((parent) => (
                  <Link
                    color="text.secondary"
                    key={parent.id}
                    href={`/workspace/${workspaceId}/entity/${parent.id}?${searchParams}`}
                  >
                    {parent.name.length > 10
                      ? `${parent.name.slice(0, 10)}...`
                      : parent.name}
                  </Link>
                ))}
                {entity.parents?.length && (
                  <Typography>
                    {entity.name.length > 10
                      ? `${entity.name.slice(0, 10)}...`
                      : entity.name}
                  </Typography>
                )}
              </Breadcrumbs>
            )}
            <IconButton
              disabled={disableActions}
              size="small"
              onClick={onMoreButtonClick}
            >
              <MoreHoriz />
            </IconButton>
          </Stack>
        </Stack>
      </Box>
    </Card>
  );
}
